/**
 * Sales flow engine.
 *
 * Decides what a document may become, what must exist before it, and whether it
 * is fit to post. All of it is driven by SalesConfig, so the same code runs a
 * consulting firm that invoices directly and a manufacturer running the full
 * quotation → order → delivery → invoice chain.
 *
 * Every transition produces an audit event. Nothing changes status silently.
 */

import { computeDocumentTax, round2 } from "./tax";
import type {
  SalesAuditEvent,
  SalesConfig,
  SalesDocStatus,
  SalesDocType,
  SalesDocument,
  SupplyType
} from "./types";
import { canTransition, DOC_LABELS, isLocked } from "./types";

export interface FlowIssue {
  severity: "error" | "warning";
  code: string;
  message: string;
  field?: string;
}

/** The chain of stages that apply, given the configuration. */
export const activeStages = (config: SalesConfig): SalesDocType[] => {
  const stages: SalesDocType[] = [];
  if (config.requireQuotation) stages.push("quotation");
  if (config.requireSalesOrder) stages.push("sales-order");
  if (config.requireDeliveryNote) stages.push("delivery-note");
  stages.push("invoice");
  return stages;
};

/**
 * What this document can be converted into next. A quotation can always become
 * an order even when orders are optional — the config decides what is
 * *required*, not what is permitted.
 */
export const allowedConversions = (
  document: Pick<SalesDocument, "type" | "status">,
  config: SalesConfig
): SalesDocType[] => {
  switch (document.type) {
    case "quotation":
      if (document.status !== "Accepted") return [];
      return config.requireSalesOrder ? ["sales-order"] : ["sales-order", "invoice"];
    case "proforma":
      return document.status === "Sent" ? ["invoice"] : [];
    case "sales-order":
      if (!["Confirmed", "Partially delivered", "Delivered"].includes(document.status)) return [];
      return config.requireDeliveryNote ? ["delivery-note"] : ["delivery-note", "invoice"];
    case "delivery-note":
      return ["Dispatched", "Delivered"].includes(document.status) ? ["invoice"] : [];
    case "invoice":
      return document.status === "Posted" || document.status === "Partially paid" || document.status === "Paid"
        ? ["credit-note", "debit-note"]
        : [];
    default:
      return [];
  }
};

/**
 * Checks that the prerequisites configured for an invoice are actually
 * satisfied. This is the switch in action: with requireSalesOrder off, an
 * invoice with no order is perfectly valid; with it on, the same invoice is
 * rejected before it can post.
 */
export const validatePrerequisites = (
  invoice: Pick<SalesDocument, "salesOrderId" | "deliveryNoteIds" | "quotationId" | "type">,
  config: SalesConfig
): FlowIssue[] => {
  const issues: FlowIssue[] = [];
  if (invoice.type !== "invoice") return issues;

  if (config.requireSalesOrder && !invoice.salesOrderId) {
    issues.push({
      severity: "error",
      code: "SO_REQUIRED",
      field: "salesOrderId",
      message: "Configuration requires every invoice to originate from a confirmed sales order."
    });
  }
  if (config.requireDeliveryNote && !(invoice.deliveryNoteIds?.length)) {
    issues.push({
      severity: "error",
      code: "DELIVERY_REQUIRED",
      field: "deliveryNoteIds",
      message: "Configuration requires goods to be delivered before an invoice is raised."
    });
  }
  if (config.requireQuotation && !invoice.quotationId && !invoice.salesOrderId) {
    issues.push({
      severity: "warning",
      code: "QUOTATION_MISSING",
      field: "quotationId",
      message: "Configuration expects a quotation to precede the sale. None is linked."
    });
  }
  return issues;
};

export interface PartnerCreditContext {
  creditLimit?: number;
  outstandingBalance: number;
  creditDays?: number;
  gstin?: string;
  gstStatus?: string;
  hasPan?: boolean;
}

/**
 * Full pre-post validation. Everything that could make an invoice wrong,
 * unfilable or unrecoverable is caught here rather than after posting.
 */
export const validateDocument = (
  document: SalesDocument,
  config: SalesConfig,
  partner?: PartnerCreditContext
): FlowIssue[] => {
  const issues: FlowIssue[] = [...validatePrerequisites(document, config)];

  if (!document.lines.length) {
    issues.push({ severity: "error", code: "NO_LINES", message: `A ${DOC_LABELS[document.type].toLowerCase()} needs at least one line.` });
  }
  if (!document.partnerId) {
    issues.push({ severity: "error", code: "NO_PARTNER", field: "partnerId", message: "Select a partner." });
  }
  if (!document.date) {
    issues.push({ severity: "error", code: "NO_DATE", field: "date", message: "Document date is required." });
  }

  for (const line of document.lines) {
    if (line.quantity <= 0) {
      issues.push({ severity: "error", code: "BAD_QUANTITY", field: line.id, message: `"${line.description}" has a quantity of zero or less.` });
    }
    if (line.unitPrice < 0) {
      issues.push({ severity: "error", code: "BAD_PRICE", field: line.id, message: `"${line.description}" has a negative unit price.` });
    }
    const gross = line.quantity * line.unitPrice;
    const discount = (line.discountPercent ? (gross * line.discountPercent) / 100 : 0) + (line.discountAmount ?? 0);
    if (discount > gross + 0.001) {
      issues.push({ severity: "error", code: "DISCOUNT_EXCEEDS", field: line.id, message: `Discount on "${line.description}" exceeds the line value.` });
    }
    if (line.taxTreatment === "taxable" && line.gstRate <= 0) {
      issues.push({ severity: "warning", code: "ZERO_RATE_TAXABLE", field: line.id, message: `"${line.description}" is marked taxable but carries a 0% rate. Set the treatment to nil-rated or exempt if that is intended.` });
    }
    // HSN is mandatory on a tax invoice; the digit count varies with turnover
    // but its absence is always a rule 46 defect.
    if (document.type === "invoice" && !line.hsnSac) {
      issues.push({ severity: "warning", code: "NO_HSN", field: line.id, message: `"${line.description}" has no HSN/SAC. GSTR-1 Table 12 requires it.` });
    }
  }

  if (document.type === "invoice" || document.type === "credit-note") {
    if (!document.placeOfSupply) {
      issues.push({ severity: "error", code: "NO_POS", field: "placeOfSupply", message: "Place of supply is mandatory under rule 46." });
    }
    // A registered recipient without a GSTIN on the invoice cannot claim ITC,
    // and the invoice lands in B2C rather than B2B in GSTR-1.
    if (partner?.gstStatus === "regular" && !document.partnerGstin) {
      issues.push({ severity: "error", code: "NO_RECIPIENT_GSTIN", field: "partnerGstin", message: "The recipient is registered but no GSTIN is on the document. They will not be able to claim input credit." });
    }
  }

  if (document.type === "credit-note") {
    if (!document.againstInvoiceId) {
      issues.push({ severity: "error", code: "NO_ORIGINAL_INVOICE", field: "againstInvoiceId", message: "A credit note must reference the original invoice." });
    }
    if (!document.creditNoteReason) {
      issues.push({ severity: "error", code: "NO_REASON", field: "creditNoteReason", message: "Select a reason — it is reported in GSTR-1." });
    }
  }

  // Credit control
  if (document.type === "invoice" && partner?.creditLimit && config.creditEnforcement !== "off") {
    const tax = computeDocumentTax(document.lines, document.charges, document.documentDiscount, {canonical:document.canonical,adjustments:document.adjustments,
      supplyType: document.supplyType,
      rcm: document.rcm,
      roundOff: config.roundOff
    });
    const exposure = round2(partner.outstandingBalance + tax.total);
    if (exposure > partner.creditLimit) {
      issues.push({
        severity: config.creditEnforcement === "block" ? "error" : "warning",
        code: "CREDIT_LIMIT",
        message: `This invoice takes the customer to ${exposure.toLocaleString("en-IN")} against a limit of ${partner.creditLimit.toLocaleString("en-IN")}.`
      });
    }
  }

  return issues;
};

export const hasBlockingIssues = (issues: FlowIssue[]): boolean =>
  issues.some((issue) => issue.severity === "error");

// ---------------------------------------------------------- Transitions

export interface TransitionResult {
  document: SalesDocument;
  event: SalesAuditEvent;
}

export interface TransitionOptions {
  user: string;
  note?: string;
  /** Supplied so results are reproducible in tests. */
  now?: string;
  eventId?: string;
}

/**
 * Moves a document to a new status, refusing anything the state machine does
 * not allow. Returns the updated document and the audit event to persist —
 * the two are produced together so a status change without a trail is not
 * expressible.
 */
export const transition = (
  document: SalesDocument,
  to: SalesDocStatus,
  options: TransitionOptions
): TransitionResult => {
  const from = document.status;
  if (!canTransition(document.type, from, to)) {
    throw new Error(`${DOC_LABELS[document.type]} ${document.number} cannot move from "${from}" to "${to}".`);
  }
  const at = options.now ?? new Date().toISOString();
  const updated: SalesDocument = {
    ...document,
    status: to,
    updatedAt: at,
    ...(to === "Posted" ? { postedAt: at } : {}),
    ...(to === "Approved" ? { reviewer: options.user } : {})
  };
  return {
    document: updated,
    event: {
      id: options.eventId ?? `evt-${at}-${document.id}`,
      documentId: document.id,
      documentType: document.type,
      documentNumber: document.number,
      action: `${DOC_LABELS[document.type]} ${to.toLowerCase()}`,
      fromStatus: from,
      toStatus: to,
      user: options.user,
      at,
      note: options.note
    }
  };
};

/**
 * The status an approved document should post into, honouring the approval
 * setting. With approval off, a draft posts directly; with it on, it must pass
 * through review first.
 */
export const nextStatusForPosting = (config: SalesConfig, current: SalesDocStatus): SalesDocStatus => {
  if (!config.requireInvoiceApproval) return "Posted";
  return current === "Draft" ? "Pending review" : current === "Pending review" ? "Approved" : "Posted";
};

/** Records an edit with field-level before/after, for the audit trail. */
export const recordEdit = (
  document: SalesDocument,
  changes: Array<{ field: string; from: unknown; to: unknown }>,
  options: TransitionOptions
): SalesAuditEvent => {
  if (isLocked(document)) {
    throw new Error(`${DOC_LABELS[document.type]} ${document.number} is ${document.status.toLowerCase()} and cannot be edited. Raise a credit note instead.`);
  }
  const at = options.now ?? new Date().toISOString();
  return {
    id: options.eventId ?? `evt-${at}-${document.id}`,
    documentId: document.id,
    documentType: document.type,
    documentNumber: document.number,
    action: "Edited",
    user: options.user,
    at,
    changes,
    note: options.note
  };
};

// ---------------------------------------------------------- Conversions

export interface ConvertOptions extends TransitionOptions {
  number: string;
  date: string;
  /** Convert only these source line ids, for partial invoicing. */
  lineIds?: string[];
  /** Quantities to carry over, keyed by source line id. */
  quantities?: Record<string, number>;
}

/**
 * Builds the target document from a source, carrying the chain links so the
 * trail from quotation to receipt is never broken.
 */
export const convert = (
  source: SalesDocument,
  target: SalesDocType,
  config: SalesConfig,
  options: ConvertOptions
): { document: SalesDocument; event: SalesAuditEvent } => {
  if (!allowedConversions(source, config).includes(target)) {
    throw new Error(`${DOC_LABELS[source.type]} ${source.number} in status "${source.status}" cannot be converted to a ${DOC_LABELS[target].toLowerCase()}.`);
  }

  const selected = options.lineIds?.length
    ? source.lines.filter((line) => options.lineIds!.includes(line.id))
    : source.lines;

  if (!selected.length) throw new Error("No lines were selected for conversion.");

  if (!config.allowPartialInvoicing && target === "invoice" && selected.length !== source.lines.length) {
    throw new Error("Partial invoicing is switched off. Invoice the whole order or enable partial invoicing in configuration.");
  }

  const lines = selected.map((line) => {
    const quantity = options.quantities?.[line.id] ?? line.quantity;
    if (quantity > line.quantity && !config.allowOverDelivery) {
      throw new Error(`Quantity for "${line.description}" exceeds the ${DOC_LABELS[source.type].toLowerCase()} quantity and over-delivery is switched off.`);
    }
    if (quantity > line.quantity) {
      const tolerance = line.quantity * (1 + config.overDeliveryTolerancePercent / 100);
      if (quantity > tolerance) {
        throw new Error(`Quantity for "${line.description}" exceeds the ${config.overDeliveryTolerancePercent}% over-delivery tolerance.`);
      }
    }
    return { ...line, id: `${line.id}-c`, quantity, sourceLineId: line.id, originLineId: line.originLineId ?? line.id };
  });

  const at = options.now ?? new Date().toISOString();
  const document: SalesDocument = {
    ...source,
    id: `${target}-${options.number}`,
    type: target,
    number: options.number,
    date: options.date,
    status: "Draft",
    lines,
    createdAt: at,
    updatedAt: at,
    postedAt: undefined,
    journalId: undefined,
    balance: undefined,
    maker: options.user,
    reviewer: undefined,
    quotationId: source.type === "quotation" ? source.id : source.quotationId,
    salesOrderId: source.type === "sales-order" ? source.id : source.salesOrderId,
    deliveryNoteIds: source.type === "delivery-note"
      ? [...(source.deliveryNoteIds ?? []), source.id]
      : source.deliveryNoteIds
  };

  return {
    document,
    event: {
      id: options.eventId ?? `evt-${at}-${document.id}`,
      documentId: document.id,
      documentType: target,
      documentNumber: options.number,
      action: `Created from ${DOC_LABELS[source.type].toLowerCase()} ${source.number}`,
      toStatus: "Draft",
      user: options.user,
      at,
      note: options.note
    }
  };
};

/**
 * How much of an order remains to be invoiced or delivered. Drives the
 * "Partially delivered" status and stops an order being over-invoiced.
 */
export interface FulfilmentLine {
  lineId: string;
  description: string;
  ordered: number;
  fulfilled: number;
  pending: number;
}

export const fulfilmentStatus = (
  order: SalesDocument,
  downstream: SalesDocument[]
): { lines: FulfilmentLine[]; complete: boolean; started: boolean } => {
  const fulfilledByLine = new Map<string, number>();
  for (const document of downstream) {
    if (document.status === "Cancelled") continue;
    for (const line of document.lines) {
      const key = line.originLineId ?? line.sourceLineId;
      if (!key) continue;
      fulfilledByLine.set(key, (fulfilledByLine.get(key) ?? 0) + line.quantity);
    }
  }
  const lines = order.lines.map((line) => {
    const fulfilled = round2(fulfilledByLine.get(line.originLineId ?? line.id) ?? 0);
    return {
      lineId: line.id,
      description: line.description,
      ordered: line.quantity,
      fulfilled,
      pending: round2(Math.max(0, line.quantity - fulfilled))
    };
  });
  return {
    lines,
    complete: lines.every((line) => line.pending <= 0.0001),
    started: lines.some((line) => line.fulfilled > 0)
  };
};

/** Derives the order status from what has actually been delivered. */
export const derivedOrderStatus = (
  order: SalesDocument,
  downstream: SalesDocument[]
): SalesDocStatus => {
  if (order.status === "Cancelled" || order.status === "Closed") return order.status;
  const { complete, started } = fulfilmentStatus(order, downstream);
  if (complete) return "Delivered";
  if (started) return "Partially delivered";
  return order.status;
};

/**
 * Supply type from the supplier's state and the place of supply. Exports and
 * SEZ supplies are inter-state by law regardless of geography.
 */
export const resolveSupplyType = (
  supplierStateCode: string,
  placeOfSupply: string,
  partnerGstStatus?: string
): SupplyType => {
  if (partnerGstStatus === "overseas") return "export";
  if (partnerGstStatus === "sez" || partnerGstStatus === "deemed-export") return "inter-state";
  return supplierStateCode === placeOfSupply ? "intra-state" : "inter-state";
};

